import React, { useState } from "react";
import { Form, Input, Button, Icon, Select, Upload, message } from "antd";
import {
  CompanyFormData,
  DocumentItem,
  DocumentDescriptionType,
  CustomerType,
  CUSTOMER_TYPE,
} from "./types";
import ClientTypeCard from "./ClientTypeCard";
import api from "../../services/api";
import axios from "axios";
import type { RcCustomRequestOptions } from "antd/lib/upload/interface";
import type { RcFile } from "antd/lib/upload/interface";

const { Option } = Select;
const { Dragger } = Upload;

function applyProgress(list: any[], uid: string, percent: number): any[] {
  return list.map((f: any) => (f.uid === uid ? { ...f, percent } : f));
}

const DOC_TYPES_BY_CUSTOMER: Record<CustomerType, DocumentDescriptionType[]> = {
  [CUSTOMER_TYPE.CONDO]: ["Nota fiscal de licença de software", "Outro"],
  [CUSTOMER_TYPE.CPO]: [
    "Contrato com a Tupi",
    "Contrato com a WeMob",
    "Outro",
  ],
};

// Descrição pré-selecionada ao anexar, quando o tipo de cliente só admite
// um documento óbvio.
const defaultDocDescription = (
  type: CustomerType | null,
): DocumentDescriptionType | null =>
  type === CUSTOMER_TYPE.CONDO ? "Nota fiscal de licença de software" : null;

const maskCNPJ = (v: string) => {
  const d = v.replace(/\D/g, "").slice(0, 14);
  return d
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
};

const formatBytes = (b: number) => {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

interface Props {
  value: CompanyFormData;
  onChange: React.Dispatch<React.SetStateAction<CompanyFormData>>;
  onBack: () => void;
  onNext: () => void;
  submitting?: boolean;
}

const MAX_DOC_SIZE_MB = 5;
const MAX_DOCS = 1;

const StepCompany: React.FC<Props> = ({
  value,
  onChange,
  onBack,
  onNext,
  submitting,
}) => {
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [uploadFileList, setUploadFileList] = useState<any[]>([]);
  const [failedUploads, setFailedUploads] = useState<RcFile[]>([]);

  const set = (patch: Partial<CompanyFormData>) =>
    onChange({ ...value, ...patch });

  // Um arquivo está sempre em exatamente um destes estados (em upload,
  // falho ou concluído), então somar os três não duplica contagem.
  const usedDocSlots =
    value.onboardingDocuments.length +
    uploadFileList.length +
    failedUploads.length;
  const docSlotsFull = usedDocSlots >= MAX_DOCS;

  const documentDigits = value.document.replace(/\D/g, "");
  const nameValid = value.companyName.trim().length >= 2;
  const documentValid = documentDigits.length === 14;
  const typeValid = !!value.customerType;
  const docsValid =
    value.onboardingDocuments.length === 0 ||
    value.onboardingDocuments.every((d) => d.description !== null);
  const canNext = nameValid && documentValid && typeValid && docsValid;

  const removeDoc = (id: string) =>
    set({
      onboardingDocuments: value.onboardingDocuments.filter((d) => d.id !== id),
    });

  const setDocDescription = (
    id: string,
    description: DocumentDescriptionType,
  ) =>
    set({
      onboardingDocuments: value.onboardingDocuments.map((d) =>
        d.id === id ? { ...d, description } : d,
      ),
    });

  const beforeUpload = (file: RcFile, fileList: RcFile[]) => {
    const slotsLeft = MAX_DOCS - usedDocSlots;
    const indexInBatch = fileList.indexOf(file);
    if (indexInBatch >= slotsLeft) {
      // Avisa apenas uma vez por lote (no primeiro arquivo excedente)
      if (indexInBatch === Math.max(slotsLeft, 0)) {
        message.warning(`Você pode anexar no máximo ${MAX_DOCS} documento.`);
      }
      return false;
    }
    if (file.type !== "application/pdf") {
      message.error("Apenas arquivos PDF são aceitos.");
      return false;
    }
    if (file.size > MAX_DOC_SIZE_MB * 1024 * 1024) {
      message.error(`O arquivo deve ter no máximo ${MAX_DOC_SIZE_MB} MB.`);
      return false;
    }
    return true;
  };

  const uploadFile = async (
    file: RcFile,
    callbacks?: {
      onProgress?: Function;
      onSuccess?: Function;
      onError?: Function;
    },
  ) => {
    try {
      const {
        data: { signedUrl, publicUrl },
      } = await api.get(`/company/document-upload/${file.name}`);

      await axios.put(signedUrl, file, {
        headers: { "Content-Type": "application/pdf" },
        onUploadProgress: ({ loaded, total }) => {
          callbacks?.onProgress?.(
            { percent: Math.round((loaded * 100) / total) },
            file,
          );
        },
      });

      const newDoc: DocumentItem = {
        id: Math.random().toString(36).slice(2),
        name: file.name,
        size: file.size,
        file: file as unknown as File,
        url: publicUrl,
        description: defaultDocDescription(value.customerType),
      };
      onChange((prev) => ({
        ...prev,
        onboardingDocuments: [...prev.onboardingDocuments, newDoc],
      }));
      setFailedUploads((prev) => prev.filter((f) => f !== file));
      setUploadFileList((prev) => prev.filter((f: any) => f.uid !== file.uid));
      callbacks?.onSuccess?.({}, file);
    } catch {
      setFailedUploads((prev) =>
        prev.includes(file) ? prev : [...prev, file],
      );
      setUploadFileList((prev) => prev.filter((f: any) => f.uid !== file.uid));
      callbacks?.onError?.(new Error("Falha no upload"));
    }
  };

  const handleUpload = ({
    onProgress,
    onError,
    onSuccess,
    file,
  }: RcCustomRequestOptions) =>
    uploadFile(file as RcFile, { onProgress, onSuccess, onError });

  const retryUpload = (file: RcFile) => {
    setFailedUploads((prev) => prev.filter((f) => f !== file));
    setUploadFileList((prev) => [
      ...prev,
      {
        uid: file.uid,
        name: file.name,
        size: file.size,
        status: "uploading",
        percent: 0,
      },
    ]);
    uploadFile(file, {
      onProgress: ({ percent }: { percent: number }) => {
        setUploadFileList((prev) => applyProgress(prev, file.uid, percent));
      },
    });
  };

  const discardFailedUpload = (file: RcFile) =>
    setFailedUploads((prev) => prev.filter((f) => f !== file));

  const handleSubmit = () => {
    setTouched({ name: true, cnpj: true, type: true, docs: true });
    if (canNext) onNext();
  };

  return (
    <div
      style={{
        padding: "32px",
        display: "flex",
        flexDirection: "column",
        gap: 24,
      }}
    >
      <div>
        <h2
          style={{
            fontSize: 22,
            fontWeight: 600,
            color: "#202020",
            marginBottom: 0,
          }}
        >
          Vamos lá! Insira os dados da sua empresa
        </h2>
      </div>

      {/* Nome + CNPJ */}
      <div className="ativacao-grid-2col">
        <Form.Item
          label="Nome da empresa"
          required
          validateStatus={touched.name && !nameValid ? "error" : ""}
          help={
            touched.name && !nameValid
              ? "Informe a razão social ou nome fantasia."
              : ""
          }
        >
          <Input
            prefix={<Icon type="bank" style={{ color: "rgba(0,0,0,.25)" }} />}
            placeholder="Ex: Condomínio Jardim das Flores"
            value={value.companyName}
            onChange={(e) => set({ companyName: e.target.value })}
            onBlur={() => setTouched((t) => ({ ...t, name: true }))}
          />
        </Form.Item>

        <Form.Item
          label="CNPJ"
          required
          validateStatus={touched.cnpj && !documentValid ? "error" : ""}
          help={
            touched.cnpj && !documentValid
              ? "Digite um CNPJ com 14 dígitos."
              : ""
          }
        >
          <Input
            placeholder="00.000.000/0000-00"
            inputMode="numeric"
            value={value.document}
            onChange={(e) => set({ document: maskCNPJ(e.target.value) })}
            onBlur={() => setTouched((t) => ({ ...t, cnpj: true }))}
          />
        </Form.Item>
      </div>

      {/* Tipo de cliente */}
      <div>
        <div style={{ marginBottom: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 500, color: "#202020" }}>
            <span style={{ color: "#5b00ef", marginRight: 4 }}>*</span>Tipo de
            cliente
          </span>
          <div style={{ fontSize: 12, color: "#6d7583" }}>
            Selecione a opção que descreve sua empresa.
          </div>
        </div>
        <div
          role="radiogroup"
          className="ativacao-grid-2col"
          style={{ gap: 12 }}
        >
          <ClientTypeCard
            icon="home"
            title="Condomínios"
            description="Instalação em edifícios residenciais ou comerciais para uso de moradores e visitantes."
            tag="Sem repasse financeiro"
            selected={value.customerType === CUSTOMER_TYPE.CONDO}
            onClick={() => set({ customerType: CUSTOMER_TYPE.CONDO })}
          />
          <ClientTypeCard
            icon="apartment"
            title="Operador de rede (CPO)"
            description="Operação de uma rede pública de estações com cobrança e repasse por sessão."
            tag="Requer dados bancários"
            tagVariant="purple"
            selected={value.customerType === CUSTOMER_TYPE.CPO}
            onClick={() => set({ customerType: CUSTOMER_TYPE.CPO })}
          />
        </div>
        {touched.type && !typeValid && (
          <div style={{ color: "#f5222d", fontSize: 12, marginTop: 4 }}>
            Selecione o tipo de cliente.
          </div>
        )}
      </div>

      {/* Upload de documentos */}
      <div>
        <div style={{ marginBottom: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 500, color: "#202020" }}>
            Documento obrigatório
          </span>
          <div style={{ fontSize: 12, color: "#6d7583" }}>
            {value.customerType === CUSTOMER_TYPE.CONDO ? (
              <span>
                Anexe a <strong>nota fiscal de licença de software.</strong>{" "}
                Tamanho máximo: {MAX_DOC_SIZE_MB} MB
              </span>
            ) : value.customerType === CUSTOMER_TYPE.CPO ? (
              <span>
                Anexe o seu <strong>contrato com a Tupi ou a WeMob.</strong>{" "}
                Tamanho máximo: {MAX_DOC_SIZE_MB} MB
              </span>
            ) : null}
          </div>
        </div>

        <Dragger
          multiple={MAX_DOCS > 1}
          accept=".pdf"
          beforeUpload={beforeUpload}
          customRequest={handleUpload as any}
          fileList={uploadFileList}
          onChange={({ fileList }) => {
            setUploadFileList(fileList.filter((f) => f.status === "uploading"));
          }}
          showUploadList={uploadFileList.length > 0}
          disabled={docSlotsFull || !typeValid}
          style={{
            borderColor: "#d0d5dd",
            opacity: docSlotsFull || !typeValid ? 0.5 : 1,
          }}
        >
          <p className="ant-upload-drag-icon">
            <Icon type="inbox" style={{ color: "#5b00ef" }} />
          </p>
          {!typeValid ? (
            <p
              className="ant-upload-text"
              style={{ color: "#6d7583", padding: "8px 16px" }}
            >
              Selecione o <strong>tipo de cliente</strong> para visualizar o
              documento a ser enviado
            </p>
          ) : (
            <>
              <p className="ant-upload-text">
                <strong>Arraste o arquivo aqui</strong> ou clique para
                selecionar
              </p>
              <p className="ant-upload-hint">
                Tamanho máximo: {MAX_DOC_SIZE_MB} MB
              </p>
            </>
          )}
        </Dragger>

        {failedUploads.length > 0 && (
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: "12px 0 0",
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {failedUploads.map((file) => (
              <li
                key={file.uid}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 12px",
                  border: "1px solid #ffa39e",
                  borderRadius: 8,
                  background: "#fff1f0",
                }}
              >
                <Icon
                  type="exclamation-circle"
                  style={{ color: "#f5222d", fontSize: 16, flexShrink: 0 }}
                />
                <span
                  style={{
                    flex: 1,
                    fontSize: 13,
                    color: "#f5222d",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {file.name}
                </span>
                <Button
                  size="small"
                  icon="reload"
                  onClick={() => retryUpload(file)}
                >
                  Tentar novamente
                </Button>
                <Button
                  size="small"
                  icon="delete"
                  onClick={() => discardFailedUpload(file)}
                  style={{ color: "#6d7583", flexShrink: 0 }}
                  title="Remover arquivo"
                />
              </li>
            ))}
          </ul>
        )}

        {value.onboardingDocuments.length > 0 && (
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: "12px 0 0",
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {value.onboardingDocuments.map((doc) => (
              <li
                key={doc.id}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  padding: "10px 12px",
                  border: "1px solid #e9ecf0",
                  borderRadius: 8,
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "40px 1fr 32px",
                    gap: 12,
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      width: 40,
                      height: 40,
                      background: "#f2f4f7",
                      borderRadius: 8,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#6d7583",
                    }}
                  >
                    <Icon type="file-text" />
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: "#202020",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {doc.name}
                    </div>
                    <div style={{ fontSize: 11, color: "#6d7583" }}>
                      {formatBytes(doc.size)}
                    </div>
                  </div>
                  <Button
                    type="link"
                    size="small"
                    icon="delete"
                    onClick={() => removeDoc(doc.id)}
                    style={{ color: "#6d7583", padding: 0 }}
                  />
                </div>
                <Form.Item
                  label="Tipo do documento"
                  required
                  validateStatus={!doc.description ? "error" : ""}
                  help={
                    touched.docs && !doc.description
                      ? "Selecione o tipo do documento."
                      : ""
                  }
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    placeholder="Selecione"
                    value={doc.description ?? undefined}
                    onChange={(v: DocumentDescriptionType) =>
                      setDocDescription(doc.id, v)
                    }
                    style={{ width: "100%" }}
                    className={!doc.description ? "ativacao-select-error" : ""}
                  >
                    {(value.customerType
                      ? DOC_TYPES_BY_CUSTOMER[value.customerType]
                      : []
                    ).map((t) => (
                      <Option key={t} value={t}>
                        {t}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Ações */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          paddingTop: 24,
          borderTop: "1px solid #e9ecf0",
          marginTop: 8,
        }}
      >
        <Button icon="arrow-left" onClick={onBack}>
          Voltar
        </Button>
        <Button
          type="primary"
          onClick={handleSubmit}
          disabled={
            !canNext ||
            submitting ||
            uploadFileList.length > 0 ||
            failedUploads.length > 0
          }
          loading={submitting || uploadFileList.length > 0}
          style={
            canNext && uploadFileList.length === 0 && failedUploads.length === 0
              ? { background: "#5b00ef", borderColor: "#5b00ef" }
              : {}
          }
        >
          {value.customerType === CUSTOMER_TYPE.CPO
            ? "Continuar"
            : "Finalizar cadastro"}
          {!submitting && <Icon type="arrow-right" />}
        </Button>
      </div>
    </div>
  );
};

export default StepCompany;
